import { queryOptions } from '@tanstack/react-query';

import { fetchBackend } from '../../apiClient';

export type ConnectedK8SCluster = {
  cluster_id: string;
  cluster_name: string;
  agent_version: string;
  last_heartbeat: string;
  init: boolean;
};

export type DiscoveredK8SCluster = {
  name: string;
  provider: 'EKS' | 'GKE';
  region: string;
  status: 'ACTIVE' | 'INACTIVE';
  available_for_connection: boolean;
  arn: string;
};

export type K8SClusterSummary = {
  total_discovered: number;
  total_connected: number;
  eks_discovered: number;
  eks_connected: number;
  gke_discovered: number;
  gke_connected: number;
};

export type K8SClustersData = {
  connected: ConnectedK8SCluster[];
  discovered: DiscoveredK8SCluster[];
  summary: K8SClusterSummary;
};

export type K8SClustersResponse = {
  clusters: ConnectedK8SCluster[];
  count: number;
  unconnected_clusters?: DiscoveredK8SCluster[];
};

// Legacy types for backward compatibility
export type K8SClusterStatus = ConnectedK8SCluster & {
  project_id: string;
};

export type UnconnectedK8SCluster = DiscoveredK8SCluster & {
  connected: false;
};

export const k8sClustersQueries = {
  all: () => ['k8s-clusters'],
  byProject: (projectId: string) => [...k8sClustersQueries.all(), projectId],
  list: (projectId: string) =>
    queryOptions({
      queryKey: [...k8sClustersQueries.byProject(projectId), 'list'],
      queryFn: async () => {
        const clusters = await listK8SClusters(projectId);

        return clusters;
      },
    }),
} as const;

export const listK8SClusters = async (
  projectId: string,
): Promise<K8SClusterStatus[]> => {
  const getResponse = await fetchBackend(`/k8s/clusters/${projectId}`, {
    method: 'GET',
  });

  if (!getResponse.ok) {
    throw new Error(`Error: ${getResponse.statusText}`);
  }

  const data = (await getResponse.json()) as K8SClustersResponse;

  return data.clusters.map(cluster => ({
    ...cluster,
    project_id: projectId,
  }));
};

export const generateK8SToken = async (projectId: string): Promise<string> => {
  const response = await fetchBackend('/k8s/clusters/generate/jwt', {
    method: 'POST',
    body: JSON.stringify({
      projectId,
    }),
  });

  if (!response.ok) {
    throw new Error(`Error: ${response.statusText}`);
  }

  const data = (await response.json()) as { token: string };

  return data.token;
};
